import { useLayoutEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useFeatureValue } from '@growthbook/growthbook-react'

import {
  getSubscriptionListAction,
  setScreenNameAction,
} from 'root-redux/actions/common'
import {
  selectCurrentVariantCohort,
  selectLanguage,
} from 'root-redux/selects/common'
import { TAppDispatch } from 'root-redux/store/store'

import { useABTest } from 'hooks/useABTest'
import { useActiveCohortIdentifier } from 'hooks/useActiveCohortIdentifier'
import { useCohortToUse } from 'hooks/useCohortToUse'
import { useVatInfo } from 'hooks/useHasVatInfo'
import { useIsCancelOfferRoute } from 'hooks/useIsCancelOfferRoute'

import {
  ABCDTestVariants,
  Cohort,
  GROWTHBOOK_EXPERIMENT,
  LUVLY_REF6_CHILD_COHORT,
  Locale,
  ScreenName,
  SubscriptionListType,
  SubscriptionTagsType,
} from 'root-constants'

export const useGetSubscriptionsList = () => {
  const dispatch: TAppDispatch = useDispatch()

  const hasVatInfo = useVatInfo()
  const cohortToUse = useCohortToUse()
  const cohort = useSelector(selectCurrentVariantCohort) as string
  const language = useSelector(selectLanguage)
  const isJapaneseLocale = language === Locale.JAPANESE

  const {
    isLuvlyRefHighPricesActive,
    isIntroSaleSoldOutTest,
    isHigherIntroRef4Active,
    isRef6NewPricingActive,
    isRef03NewPricingActive,
    isIntro4SaleHighPricesActive,
  } = useABTest()
  const { isRefundCohort, isLuvlyIntroSaleCohort, isRefund4Cohort } =
    useActiveCohortIdentifier()

  const isCancelOfferRoute = useIsCancelOfferRoute()

  const refNewPricingVariant = useFeatureValue(
    GROWTHBOOK_EXPERIMENT.LV_1530,
    ABCDTestVariants.VARIANT_A,
  )

  const screenName = useMemo(() => {
    if (cohortToUse === Cohort.LUVLY_EMAIL_7) return ScreenName.EMAIL_OFFER

    return isCancelOfferRoute
      ? ScreenName.CANCEL_OFFER_LONG
      : ScreenName.PURCHASE
  }, [cohortToUse, isCancelOfferRoute])

  useLayoutEffect(() => {
    dispatch(setScreenNameAction(screenName))

    const pricesTags: SubscriptionTagsType[] = []

    hasVatInfo
      ? pricesTags.push(SubscriptionTagsType.TAX)
      : pricesTags.push(SubscriptionTagsType.NO_TAX)

    isCancelOfferRoute && pricesTags.push(SubscriptionTagsType.CANCEL_OFFER)

    isLuvlyRefHighPricesActive &&
      pricesTags.push(SubscriptionTagsType.HIGH_PRICES_LUVLY_REF)

    isIntroSaleSoldOutTest &&
      isLuvlyIntroSaleCohort &&
      pricesTags.push(SubscriptionTagsType.THREE_MONTHS_SOLDOUT)

    isHigherIntroRef4Active &&
      isRefund4Cohort &&
      pricesTags.push(SubscriptionTagsType.HIGHER_INTRO_REF4)

    isRef03NewPricingActive && pricesTags.push(SubscriptionTagsType.REF03_B)

    refNewPricingVariant === ABCDTestVariants.VARIANT_B &&
      pricesTags.push(SubscriptionTagsType.NEW_PRICES_REF_B)

    refNewPricingVariant === ABCDTestVariants.VARIANT_C &&
      pricesTags.push(SubscriptionTagsType.NEW_PRICES_REF_C)

    isRef6NewPricingActive &&
      cohort === LUVLY_REF6_CHILD_COHORT &&
      pricesTags.push(SubscriptionTagsType.REF6_NEW_PRICE)

    isJapaneseLocale &&
      isIntro4SaleHighPricesActive &&
      pricesTags.push(SubscriptionTagsType.INTRO4_SALE_HIGH_JA)

    dispatch(
      getSubscriptionListAction(
        SubscriptionListType.PURCHASE,
        pricesTags.join(','),
      ),
    )
  }, [
    dispatch,
    hasVatInfo,
    isCancelOfferRoute,
    isRefundCohort,
    screenName,
    isLuvlyRefHighPricesActive,
    isIntroSaleSoldOutTest,
    isLuvlyIntroSaleCohort,
    isHigherIntroRef4Active,
    isRefund4Cohort,
    refNewPricingVariant,
    isRef6NewPricingActive,
    cohortToUse,
    cohort,
    isRef03NewPricingActive,
    isIntro4SaleHighPricesActive,
    isJapaneseLocale,
  ])
}
